import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import { uploadUrl } from '../utils/uploads';
import { ONBOARDING_STATUS_LABELS } from '../utils/statusConstants';

const DOC_TYPES = [
  { type: 'coi', label: 'Certificate of Insurance (COI)' },
  { type: 'business_license', label: 'Business license' },
  { type: 'background_check_auth', label: 'Background check authorization' },
];

const PLACEHOLDER_AGREEMENT = `INDEPENDENT CONTRACTOR AGREEMENT (PLACEHOLDER)

This is a draft agreement for development and testing. Your final vendor agreement will be provided before launch.

By accepting, you acknowledge that you:
• Provide cleaning services as an independent contractor, not an employee
• Maintain valid insurance and required licenses for your work
• Follow site access and safety guidelines provided for each job
• Submit accurate before/after documentation when completing jobs
• Treat customer property with care and respect confidentiality

A formal agreement with full legal terms will replace this placeholder when e-sign is configured.`;

const isImage = (filename) => /\.(jpe?g|png|webp|gif)$/i.test(filename || '');
const isPdf = (filename) => /\.pdf$/i.test(filename || '');

const DocumentCard = ({ type, label, doc, onUpload, onDelete, busy, readOnly = false }) => {
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(type, file);
    e.target.value = '';
  };

  return (
    <li className="border border-slate-200 rounded-xl p-4">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-medium text-slate-800">{label}</p>
            {!doc ? (
              <p className="text-sm text-slate-500 mt-0.5">PDF or image, max 10 MB</p>
            ) : (
              <p className="text-sm text-slate-500 mt-0.5">
                Uploaded {new Date(doc.uploaded_at).toLocaleString()}
              </p>
            )}
          </div>
          {!readOnly && (
            <div className="flex flex-wrap gap-2">
              <label
                className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium cursor-pointer ${
                  busy ? 'bg-slate-100 text-slate-400 pointer-events-none' : 'bg-teal-700 text-white hover:bg-teal-800'
                }`}
              >
                {busy ? 'Uploading…' : doc ? 'Replace' : 'Upload'}
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  disabled={busy}
                  onChange={handleFile}
                />
              </label>
              {doc && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onDelete(type, label)}
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {busy ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
          )}
        </div>

        {doc && (
          <div className="flex flex-col sm:flex-row gap-4 bg-slate-50 rounded-lg p-4 border border-slate-100">
            <div className="shrink-0">
              {isImage(doc.original_filename) ? (
                <a href={uploadUrl(doc.url)} target="_blank" rel="noreferrer">
                  <img
                    src={uploadUrl(doc.url)}
                    alt={doc.original_filename}
                    className="h-32 w-full sm:w-40 object-cover rounded-lg border border-slate-200 bg-white"
                  />
                </a>
              ) : (
                <a
                  href={uploadUrl(doc.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex h-32 w-full sm:w-40 flex-col items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                >
                  <span className="text-3xl mb-1">{isPdf(doc.original_filename) ? '📄' : '📎'}</span>
                  <span className="text-xs font-medium uppercase tracking-wide">
                    {isPdf(doc.original_filename) ? 'PDF' : 'File'}
                  </span>
                </a>
              )}
            </div>
            <div className="flex-1 min-w-0 flex flex-col justify-center">
              <p className="font-medium text-slate-800 truncate" title={doc.original_filename}>
                {doc.original_filename || 'Uploaded document'}
              </p>
              <p className="text-sm text-slate-500 mt-1 capitalize">{doc.status || 'uploaded'}</p>
              <a
                href={uploadUrl(doc.url)}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-teal-700 font-medium hover:underline mt-3 inline-block"
              >
                Open full document →
              </a>
            </div>
          </div>
        )}
      </div>
    </li>
  );
};

const Onboarding = () => {
  const [params] = useSearchParams();
  const mockRequestId = params.get('request');
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [acceptingAgreement, setAcceptingAgreement] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [docBusy, setDocBusy] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    apiFetch('/onboarding')
      .then((r) => setState(r.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!mockRequestId) return;
    apiFetch('/onboarding/agreement/complete', {
      method: 'POST',
      body: JSON.stringify({ request_id: mockRequestId }),
    })
      .then(() => {
        toast.success('Agreement signed');
        load();
      })
      .catch((err) => toast.error(err.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockRequestId]);

  const setBusy = (type, value) => setDocBusy((b) => ({ ...b, [type]: value }));

  const uploadDoc = async (type, file) => {
    setBusy(type, true);
    const fd = new FormData();
    fd.append('document_type', type);
    fd.append('file', file);
    try {
      await apiFetch('/onboarding/documents', { method: 'POST', body: fd });
      toast.success('Document saved');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(type, false);
    }
  };

  const deleteDoc = async (type, label) => {
    if (!window.confirm(`Remove ${label}? You can upload a new file afterward.`)) return;
    setBusy(type, true);
    try {
      await apiFetch(`/onboarding/documents/${type}`, { method: 'DELETE' });
      toast.success('Document removed');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(type, false);
    }
  };

  const acceptPlaceholderAgreement = async () => {
    if (!agreementChecked) {
      toast.error('Check the box to confirm you accept the agreement');
      return;
    }
    setAcceptingAgreement(true);
    try {
      await apiFetch('/onboarding/agreement/accept', {
        method: 'POST',
        body: JSON.stringify({ accepted: true }),
      });
      toast.success('Agreement accepted');
      setAgreementChecked(false);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAcceptingAgreement(false);
    }
  };

  const submitForReview = async () => {
    setSubmitting(true);
    try {
      await apiFetch('/onboarding/submit', { method: 'POST' });
      toast.success('Submitted for approval');
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="text-slate-500">Loading onboarding…</p>;
  if (!state) return null;

  const { vendor, documents, checklist, can_submit, agreement_required, esign_provider } = state;
  const statusLabel = ONBOARDING_STATUS_LABELS[vendor.onboarding_status] || vendor.onboarding_status;
  const docsByType = Object.fromEntries((documents || []).map((d) => [d.document_type, d]));
  const agreementSigned = checklist.agreement.status === 'signed';
  const showPlaceholderAgreement =
    agreement_required && esign_provider === 'mock' && !agreementSigned;

  const submitHint = agreement_required
    ? 'Upload all three documents and accept the agreement to submit.'
    : 'Upload all three documents to submit. Agreement signing will be added later.';

  return (
    <div>
      <h1 className="text-2xl font-semibold text-teal-800">Vendor onboarding</h1>
      <p className="text-slate-600 mt-1 mb-6">
        Status: <span className="font-medium">{statusLabel}</span>
      </p>

      {vendor.onboarding_status === 'approved' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800">You are approved. You can now accept and complete jobs.</p>
          <Link to="/jobs" className="text-teal-700 font-medium mt-2 inline-block hover:underline">
            Go to my jobs →
          </Link>
        </div>
      )}

      {vendor.onboarding_status === 'pending_approval' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-amber-900">
          Your application is under review. We will notify you when approved.
        </div>
      )}

      {vendor.onboarding_status === 'pending_approval' && documents?.length > 0 && (
        <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h2 className="font-semibold text-slate-800 mb-4">Submitted documents</h2>
          <ul className="space-y-4">
            {DOC_TYPES.map(({ type, label }) => {
              const doc = docsByType[type];
              if (!doc) return null;
              return (
                <DocumentCard key={type} type={type} label={label} doc={doc} readOnly />
              );
            })}
          </ul>
        </section>
      )}

      {vendor.onboarding_status === 'rejected' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-900">
          Application rejected: {vendor.rejected_reason || 'Please contact support.'}
          <p className="text-sm mt-2">Update your documents below and resubmit.</p>
        </div>
      )}

      {['draft', 'rejected'].includes(vendor.onboarding_status) && (
        <>
          <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h2 className="font-semibold text-slate-800 mb-1">Required documents</h2>
            <p className="text-sm text-slate-500 mb-4">
              Upload each file below. You can preview, replace, or remove a document before submitting.
            </p>
            <ul className="space-y-4">
              {DOC_TYPES.map(({ type, label }) => (
                <DocumentCard
                  key={type}
                  type={type}
                  label={label}
                  doc={docsByType[type]}
                  onUpload={uploadDoc}
                  onDelete={deleteDoc}
                  busy={docBusy[type]}
                />
              ))}
            </ul>
          </section>

          {agreement_required ? (
            <section className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
              <h2 className="font-semibold text-slate-800 mb-2">Vendor agreement</h2>
              {agreementSigned ? (
                <p className="text-sm text-green-700 font-medium">
                  Agreement accepted
                  {checklist.agreement.signed_at &&
                    ` · ${new Date(checklist.agreement.signed_at).toLocaleString()}`}
                </p>
              ) : showPlaceholderAgreement ? (
                <>
                  <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
                    Placeholder agreement for testing — replace with your final contract before launch.
                  </p>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap mb-4">
                    {PLACEHOLDER_AGREEMENT}
                  </div>
                  <label className="flex items-start gap-3 text-sm text-slate-700 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreementChecked}
                      onChange={(e) => setAgreementChecked(e.target.checked)}
                      className="mt-1"
                    />
                    <span>I have read and accept this agreement</span>
                  </label>
                  <button
                    type="button"
                    onClick={acceptPlaceholderAgreement}
                    disabled={acceptingAgreement || !agreementChecked}
                    className="bg-teal-700 text-white px-4 py-2 rounded-lg hover:bg-teal-800 text-sm disabled:opacity-50"
                  >
                    {acceptingAgreement ? 'Saving…' : 'Accept agreement'}
                  </button>
                </>
              ) : (
                <p className="text-sm text-slate-600">
                  E-sign will be available once your agreement is configured.
                </p>
              )}
            </section>
          ) : (
            <section className="bg-slate-50 border border-slate-200 rounded-xl p-5 mb-6 text-sm text-slate-600">
              <p className="font-medium text-slate-800 mb-1">Vendor agreement — coming soon</p>
              <p>
                Agreement signing is not required while we finalize the contract. Upload your three
                documents above, then submit for approval.
              </p>
            </section>
          )}

          <button
            type="button"
            disabled={!can_submit || submitting}
            onClick={submitForReview}
            className="bg-teal-700 text-white px-6 py-2.5 rounded-lg hover:bg-teal-800 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit for approval'}
          </button>
          {!can_submit && <p className="text-sm text-slate-500 mt-2">{submitHint}</p>}
        </>
      )}
    </div>
  );
};

export default Onboarding;
